import React, { useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon, TrendingUp as TrendingUpIcon } from '@mui/icons-material';
import { useGlobal } from '../contexts/GlobalContext';

const SpamModerationSection = ({ onUpdate, boostInput = false }) => {
  const { accountId: defaultAccountId } = useGlobal();
  const [currentSpamScore, setCurrentSpamScore] = useState('');
  const [currentSpamOperator, setCurrentSpamOperator] = useState('');
  const [currentSpamThreshold, setCurrentSpamThreshold] = useState('');
  const [boost, setBoost] = useState(1);
  const [useWhitelists, setUseWhitelists] = useState(false);
  const [consoleAccountId, setConsoleAccountId] = useState(defaultAccountId || '');

  const spamScoreOptions = [
    { id: 'score_spam', label: 'Item Spam Score' },
    { id: 'user_score_spam', label: 'User Spam Score' },
    { id: 'user_events_num', label: "User's Num Events" },
    { id: 'user_events_per_day', label: "User's Events/Day" },
    { id: 'score_not_ok', label: 'Score Not-Ok' },
    { id: 'user_warpcast_spam', label: 'Warpcast Spam Label' }
  ];

  const operatorOptions = [
    { id: '<=', label: '<=' },
    { id: '>=', label: '>=' }
  ];

  const handleSpamScoreChange = (event) => {
    setCurrentSpamScore(event.target.value);
    
    // Set default operator and threshold based on score type
    if (event.target.value === 'score_spam') {
      setCurrentSpamOperator('<=');
      setCurrentSpamThreshold('0.5');
    } else if (event.target.value === 'user_score_spam') {
      setCurrentSpamOperator('<=');
      setCurrentSpamThreshold('0.5');
    } else if (event.target.value === 'user_events_num') {
      setCurrentSpamOperator('>=');
      setCurrentSpamThreshold('5');
    } else if (event.target.value === 'user_events_per_day') {
      setCurrentSpamOperator('<=');
      setCurrentSpamThreshold('150');
    } else if (event.target.value === 'score_not_ok') {
      setCurrentSpamOperator('<=');
      setCurrentSpamThreshold('0.05');
    } else if (event.target.value === 'user_warpcast_spam') {
      setCurrentSpamOperator('>=');
      setCurrentSpamThreshold('2');
    }
  };

  const handleSpamModerationAction = (actionType) => {
    if (!currentSpamScore || !currentSpamOperator || !currentSpamThreshold) return;
    
    const thresholdFloat = parseFloat(currentSpamThreshold);
    if (isNaN(thresholdFloat) || thresholdFloat < 0) return;
    
    if (useWhitelists && consoleAccountId.trim()) {
      // Use bool filter combining numeric with whitelisted users and items
      const updateData = {
        action: actionType,
        filter: 'custom',
        value: {
          bool: {
            should: [
              {
                range: {
                  [currentSpamScore]: {
                    [currentSpamOperator === '<=' ? 'lte' : 'gte']: thresholdFloat
                  }
                }
              },
              {
                terms: {
                  user_id: {
                    index: 'console-accounts',
                    id: consoleAccountId.trim(),
                    path: 'include_user_ids'
                  }
                }
              },
              {
                terms: {
                  item_id: {
                    index: 'console-accounts',
                    id: consoleAccountId.trim(),
                    path: 'include_item_ids'
                  }
                }
              }
            ],
            minimum_should_match: 1
          }
        }
      };
      
      if (boostInput) {
        updateData.boost = boost;
      }
      
      onUpdate(updateData);
    } else {
      // Use simple numeric filter
      const updateData = {
        action: actionType,
        filter: 'numeric',
        field: currentSpamScore,
        value: thresholdFloat,
        operator: currentSpamOperator
      };
      
      if (boostInput) {
        updateData.boost = boost;
      }
      
      onUpdate(updateData);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <FormControl sx={{ flex: 2 }}>
          <InputLabel>Score</InputLabel>
          <Select
            value={currentSpamScore}
            label="Score"
            onChange={handleSpamScoreChange}
          >
            {spamScoreOptions.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ flex: 1 }}>
          <InputLabel>Operator</InputLabel>
          <Select
            value={currentSpamOperator}
            label="Operator"
            onChange={(e) => setCurrentSpamOperator(e.target.value)}
          >
            {operatorOptions.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Threshold"
          type="number"
          value={currentSpamThreshold}
          onChange={(e) => setCurrentSpamThreshold(e.target.value)}
          sx={{ flex: 1 }}
        />
      </Box>
      
      {boostInput && (
        <TextField
          fullWidth
          sx={{ mb: 2 }}
          label="Boost"
          type="number"
          value={boost}
          onChange={(e) => setBoost(parseFloat(e.target.value) || 1)}
        />
      )}
      
      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={useWhitelists}
              onChange={(e) => setUseWhitelists(e.target.checked)}
            />
          }
          label="Do not apply to whitelisted users and items"
        />
      </Box>
      
      {useWhitelists && (
        <TextField
          fullWidth
          sx={{ mb: 2 }}
          label="Console Account ID"
          value={consoleAccountId}
          onChange={(e) => setConsoleAccountId(e.target.value)}
          placeholder="Enter console account ID"
        />
      )}
      
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleSpamModerationAction('include')}
          disabled={!currentSpamScore || !currentSpamOperator || !currentSpamThreshold || (useWhitelists && !consoleAccountId.trim())}
        >
          <AddIcon />
        </Button>
        
        {boostInput && (
          <Button
            variant="contained"
            color="success"
            onClick={() => handleSpamModerationAction('boost')}
            disabled={!currentSpamScore || !currentSpamOperator || !currentSpamThreshold || (useWhitelists && !consoleAccountId.trim())}
          >
            <TrendingUpIcon />
          </Button>
        )}
        
        <Button
          variant="contained"
          color="error"
          onClick={() => handleSpamModerationAction('exclude')}
          disabled={!currentSpamScore || !currentSpamOperator || !currentSpamThreshold || (useWhitelists && !consoleAccountId.trim())}
        >
          <RemoveIcon />
        </Button>
      </Box>
    </Box>
  );
};

export default SpamModerationSection;
